import { useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const GALLERY_IMAGES = [
  // Toiture
  {
    url: "https://lldls-toiture.be/wp-content/uploads/2025/11/image00021.jpeg",
    category: "toiture",
    alt: "Rénovation toiture - LLDLS"
  },
  {
    url: "https://lldls-toiture.be/wp-content/uploads/2025/11/image00092.jpeg",
    category: "toiture",
    alt: "Toiture rénovée - LLDLS"
  },
  {
    url: "https://lldls-toiture.be/wp-content/uploads/2025/11/image00031.jpeg",
    category: "toiture",
    alt: "Chantier toiture - LLDLS"
  },
  {
    url: "https://lldls-toiture.be/wp-content/uploads/2025/11/image00060.jpeg",
    category: "toiture",
    alt: "Rénovation complète - LLDLS"
  },
  {
    url: "https://lldls-toiture.be/wp-content/uploads/2025/11/image00090.jpeg",
    category: "toiture",
    alt: "Toiture neuve - LLDLS"
  },
  // Photovoltaïque
  {
    url: "https://lldls-toiture.be/wp-content/uploads/2025/11/image00067.jpeg",
    category: "photovoltaique",
    alt: "Installation panneaux solaires - LLDLS"
  },
  {
    url: "https://lldls-toiture.be/wp-content/uploads/2025/11/image00101.jpeg",
    category: "photovoltaique",
    alt: "Panneaux photovoltaïques - LLDLS"
  },
  {
    url: "https://lldls-toiture.be/wp-content/uploads/2025/11/image00044.jpeg",
    category: "photovoltaique",
    alt: "Installation solaire - LLDLS"
  },
  // Pompes à chaleur
  {
    url: "https://customer-assets.emergentagent.com/job_lead-capture-roof/artifacts/9ylnh1yo_IMG_5413.jpeg",
    category: "pac",
    alt: "Pompe à chaleur salon - LLDLS"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_lead-capture-roof/artifacts/hpjib8xh_IMG_5414.jpeg",
    category: "pac",
    alt: "Installation climatisation intérieure - LLDLS"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_lead-capture-roof/artifacts/v93byto7_IMG_5415.jpeg",
    category: "pac",
    alt: "Unité intérieure chambre - LLDLS"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_lead-capture-roof/artifacts/ubnqdefg_IMG_5416.jpeg",
    category: "pac",
    alt: "LG Dual Inverter détail - LLDLS"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_lead-capture-roof/artifacts/e1994ch8_IMG_5418.jpeg",
    category: "pac",
    alt: "Unité extérieure LG - LLDLS"
  }
];

const GallerySection = ({ defaultFilter = "all" }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState(
    defaultFilter === "Toiture" ? "toiture" : 
    defaultFilter === "Photovoltaïque" ? "photovoltaique" : 
    defaultFilter === "PAC" ? "pac" : 
    "all"
  );

  const filteredImages = filter === "all" 
    ? GALLERY_IMAGES 
    : GALLERY_IMAGES.filter(img => img.category === filter);

  const openLightbox = (index) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % filteredImages.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
  };

  return (
    <section id="realisations" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Nos réalisations
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Découvrez quelques-uns de nos chantiers réalisés en Wallonie et Brabant Wallon
          </p>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => setFilter("all")}
              className={`px-5 py-2 rounded-full font-medium transition-all ${
                filter === "all"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid="filter-all"
            >
              Tous
            </button>
            <button
              onClick={() => setFilter("toiture")}
              className={`px-5 py-2 rounded-full font-medium transition-all ${
                filter === "toiture"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid="filter-toiture"
            >
              Toiture
            </button>
            <button
              onClick={() => setFilter("photovoltaique")}
              className={`px-5 py-2 rounded-full font-medium transition-all ${
                filter === "photovoltaique"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid="filter-pv"
            >
              Photovoltaïque
            </button>
            <button
              onClick={() => setFilter("pac")}
              className={`px-5 py-2 rounded-full font-medium transition-all ${
                filter === "pac"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid="filter-pac"
            >
              Pompe à chaleur
            </button>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredImages.map((image, index) => (
            <div
              key={index}
              className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group"
              onClick={() => openLightbox(index)}
              data-testid={`gallery-image-${index}`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/40 transition-all duration-300 flex items-center justify-center">
                <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity font-medium">
                  Voir
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href="#form"
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:-translate-y-1 shadow-lg shadow-amber-500/25"
            data-testid="gallery-cta"
          >
            Demander un devis pour votre projet
          </a>
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage !== null && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-amber-500 transition-colors"
            data-testid="lightbox-close"
          >
            <X className="w-8 h-8" />
          </button>
          
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 text-white hover:text-amber-500 transition-colors"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-10 h-10" />
          </button>

          <img
            src={filteredImages[selectedImage].url}
            alt={filteredImages[selectedImage].alt}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />

          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 text-white hover:text-amber-500 transition-colors"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-10 h-10" />
          </button>

          <div className="absolute bottom-4 text-white text-sm">
            {selectedImage + 1} / {filteredImages.length}
          </div>
        </div>
      )}
    </section>
  );
};

export default GallerySection;
